import { RACE_TYPES, RACE_TYPE_LABELS } from "system";
import { RACE_TYPE_COLOR_VAR } from "~/lib/utils";

// Tire compound slip curves as the game stores them: per compound, per surface
// and per force direction, one curve at a light wheel load and one at a heavy
// load, plus the scales that turn the unitless curve into grip.
// The curve rows themselves are shared between compounds, so the JSON keeps
// one table of unique curves and every block references it by index.

export const SURFACES = ["asphalt", "offroad", "snow"] as const;
export type Surface = (typeof SURFACES)[number];
export const SURFACE_LABELS = {
  asphalt: "Asphalt",
  offroad: "Offroad",
  snow: "Snow",
} satisfies Record<Surface, string>;

export const DIRECTIONS = ["lat", "acc", "brk"] as const;
export type Direction = (typeof DIRECTIONS)[number];
export const DIRECTION_LABELS = {
  lat: "Lateral",
  acc: "Accel",
  brk: "Decel",
} satisfies Record<Direction, string>;

// How far along each curve the page shows. The lateral and drive curves are
// shown whole; the brake table runs to a slip ratio of 1.1, past a locked
// wheel at 1.0, so the page stops there and drops the table's headroom.
// If a probe ever shows brake slip above 1.0, raise this to 1.1.
export const SLIP_SHOWN = {
  lat: 49.5,
  acc: 1.1,
  brk: 1.0,
} satisfies Record<Direction, number>;

// Ceiling of the grip axis, in G, fixed so that moving a control shows as the
// curves moving rather than the axis sliding under them. The first clears every
// compound except drag, in every direction, on every surface, anywhere in the
// load, pressure and temperature ranges, wet or dry. Drag tires pull far enough
// past the rest under power to need their own ceiling, which is why selecting
// one is the only thing that rescales the axis; braking and cornering they stay
// under the shared ceiling, so nothing moves there. `check-tire-compounds.ts`
// measures both against the data.
export const GRIP_MAX = 2.1;
export const GRIP_MAX_DRAG_ACCEL = 2.7;

// The race a compound is built for decides its line colour.
// The four race types reuse the app's race-type colours;
// the rest take a tier colour that reads as the discipline.
export const COMPOUND_KINDS = [...RACE_TYPES, "drag", "drift", "snow", "vintage", "stock"] as const;
export type CompoundKind = (typeof COMPOUND_KINDS)[number];

export const COMPOUND_KIND_LABELS = {
  ...RACE_TYPE_LABELS,
  drag: "Drag",
  drift: "Drift",
  snow: "Snow",
  vintage: "Vintage",
  stock: "Stock radials",
} satisfies Record<CompoundKind, string>;

export const COMPOUND_KIND_COLOR_VAR = {
  ...RACE_TYPE_COLOR_VAR,
  drag: "var(--color-tier-red)",
  drift: "var(--color-tier-pink)",
  snow: "var(--color-tier-white)",
  vintage: "var(--color-tier-yellow)",
  stock: "var(--color-tier-gray)",
} satisfies Record<CompoundKind, string>;

export interface TireCompound {
  id: number;
  name: string;
  kind: CompoundKind;
  // One line for the picker, at most 30 characters.
  blurb: string;
  example?: string;
}

// Every compound the game ships, grouped by kind.
// Order within a kind matters: the first entry draws in the pure kind colour
// and the following ones take the lighter, darker and dashed variants.
export const TIRE_COMPOUNDS: readonly TireCompound[] = [
  { id: 1, name: "Slick", kind: "road", blurb: "Slick upgrade, top road grip" },
  { id: 2, name: "Semi-Slick", kind: "road", blurb: "Semi-Slick upgrade" },
  {
    id: 3,
    name: "Stock (Racing Slick)",
    kind: "road",
    blurb: "Stock slick on race cars",
    example: "Ferrari FXX K '16",
  },
  {
    id: 4,
    name: "Stock (Slicks Hard)",
    kind: "road",
    blurb: "Stock hard slick, race cars",
    example: "Lamborghini Sesto Elemento '11",
  },
  {
    id: 5,
    name: "Stock (Retro DOT Competition)",
    kind: "road",
    blurb: "Stock on retro race cars",
    example: "Honda NSX-R GT '05",
  },

  { id: 6, name: "Sport", kind: "street", blurb: "Sport upgrade" },
  { id: 7, name: "Street (High Performance)", kind: "street", blurb: "Street upgrade, most cars" },
  {
    id: 8,
    name: "Street (Ultra High Performance)",
    kind: "street",
    blurb: "Street upgrade, faster cars",
  },
  {
    id: 9,
    name: "Stock (DOT Sport)",
    kind: "street",
    blurb: "Stock tire on sports cars",
    example: "McLaren Artura '23",
  },

  { id: 10, name: "Offroad", kind: "cross_country", blurb: "Offroad upgrade" },
  {
    id: 11,
    name: "Stock (Off Road High Performance)",
    kind: "cross_country",
    blurb: "Stock on fast offroaders",
    example: "Ford F-150 Raptor R '23",
  },
  {
    id: 12,
    name: "Stock (Offroad Performance)",
    kind: "cross_country",
    blurb: "Stock on offroad sports cars",
    example: "Jeep Wrangler Rubicon '12",
  },
  {
    id: 13,
    name: "Stock (All Terrain)",
    kind: "cross_country",
    blurb: "Stock all-terrain tire",
    example: "Land Rover Defender '20",
  },
  {
    id: 14,
    name: "Stock (Off Road Mud)",
    kind: "cross_country",
    blurb: "Stock mud tire",
    example: "Ford Bronco Raptor '22",
  },
  {
    id: 15,
    name: "Stock (Off Road Mud Sport)",
    kind: "cross_country",
    blurb: "Stock mud tire, sport",
    example: "Rivian R1T '22",
  },
  {
    id: 16,
    name: "Stock (Extreme Offroad Buggy)",
    kind: "cross_country",
    blurb: "Stock on offroad buggies",
    example: "Can-Am Maverick X RS '18",
  },
  {
    id: 17,
    name: "Stock (Offroad Bias Ply)",
    kind: "cross_country",
    blurb: "Stock on old offroaders",
    example: "Toyota FJ40 '75",
  },

  { id: 18, name: "Rally", kind: "dirt", blurb: "Rally upgrade" },
  {
    id: 19,
    name: "Stock (RallyX)",
    kind: "dirt",
    blurb: "Stock on rallycross cars",
    example: "Ford Fiesta GRC '17",
  },
  {
    id: 20,
    name: "Stock (Retro Rally)",
    kind: "dirt",
    blurb: "Stock on retro rally cars",
    example: "Audi Sport quattro S1 '86",
  },
  {
    id: 21,
    name: "Stock (Classic Rally)",
    kind: "dirt",
    blurb: "Stock on classic rally cars",
    example: "Ford Escort RS1800 '77",
  },

  { id: 22, name: "Drag", kind: "drag", blurb: "Drag upgrade, post-patch" },
  { id: 23, name: "Drag (pre-patch)", kind: "drag", blurb: "Before the drag meta got patched" },

  { id: 24, name: "Drift", kind: "drift", blurb: "Drift upgrade" },
  { id: 25, name: "Stock (Formula Drift)", kind: "drift", blurb: "Stock on Formula Drift cars" },

  { id: 26, name: "Snow", kind: "snow", blurb: "Snow upgrade" },

  { id: 27, name: "Vintage Race", kind: "vintage", blurb: "Vintage Race upgrade" },
  { id: 28, name: "Vintage White Wall", kind: "vintage", blurb: "Vintage White Wall upgrade" },
  {
    id: 29,
    name: "Stock (Vintage Race)",
    kind: "vintage",
    blurb: "Stock on vintage race cars",
    example: "Lancia Stratos HF '74",
  },
  {
    id: 30,
    name: "Stock (Classic 60s Race Bias)",
    kind: "vintage",
    blurb: "Stock on 60s race cars",
    example: "Mercedes-Benz 300 SLR '55",
  },
  {
    id: 31,
    name: "Stock (Classic High Performance Bias)",
    kind: "vintage",
    blurb: "Stock on classic performance",
    example: "Nissan Skyline GT-R '73",
  },
  {
    id: 32,
    name: "Stock (Classic Performance Bias)",
    kind: "vintage",
    blurb: "Stock on classic sports cars",
    example: "Toyota 2000GT '69",
  },
  {
    id: 33,
    name: "Stock (Classic Muscle Bias)",
    kind: "vintage",
    blurb: "Stock on classic muscle",
    example: "Chevrolet Camaro Z28 '79",
  },
  {
    id: 34,
    name: "Stock (Classic Muscle Bias Low Perf)",
    kind: "vintage",
    blurb: "Stock on classic muscle, low",
    example: "Ford Mustang '65",
  },
  {
    id: 35,
    name: "Stock (Vintage Bias Ply)",
    kind: "vintage",
    blurb: "Stock on vintage cars",
    example: "Datsun 510 '70",
  },

  {
    id: 36,
    name: "Stock (Radial High)",
    kind: "stock",
    blurb: "Stock everyday tire, best",
    example: "Toyota GT86 '13",
  },
  {
    id: 37,
    name: "Stock (Radial Medium-High)",
    kind: "stock",
    blurb: "Stock everyday tire, upper",
    example: "Honda Civic Type R '97",
  },
  {
    id: 38,
    name: "Stock (Radial Medium)",
    kind: "stock",
    blurb: "Stock everyday tire, mid",
    example: "Mitsubishi Galant VR-4 '92",
  },
  {
    id: 39,
    name: "Stock (Radial Medium-Low)",
    kind: "stock",
    blurb: "Stock everyday tire",
    example: "Mazda MX-5 Miata '94",
  },
  {
    id: 40,
    name: "Stock (Radial Low)",
    kind: "stock",
    blurb: "Stock everyday tire, lowest",
    example: "Chevrolet Monte Carlo SS '88",
  },
];

export const TIRE_COMPOUND_BY_ID: ReadonlyMap<number, TireCompound> = new Map(
  TIRE_COMPOUNDS.map((c) => [c.id, c]),
);

export interface CompoundStroke {
  color: string;
  dash: string | undefined;
}

// Compounds of one kind share a hue and differ by shade first, then by dash,
// so up to nine of a kind stay apart on the chart.
const SHADES = [0, 35, -35] as const;
const DASHES = [undefined, "7 4", "2 3"] as const;

export function compoundStroke(compound: TireCompound): CompoundStroke {
  const siblings = TIRE_COMPOUNDS.filter((c) => c.kind === compound.kind);
  const i = siblings.findIndex((c) => c.id === compound.id);
  const base = COMPOUND_KIND_COLOR_VAR[compound.kind];
  const shade = SHADES[i % SHADES.length] ?? 0;
  const color =
    shade === 0
      ? base
      : `color-mix(in oklab, ${base} ${100 - Math.abs(shade)}%, ${shade > 0 ? "white" : "black"})`;
  return { color, dash: DASHES[Math.floor(i / SHADES.length)] };
}

// ── Curve data ────────────────────────────────────────────────────────────────

interface CurveBlockJson {
  maxslip: number;
  ds: number;
  // [curve index, curve scale] for the light load and the heavy load.
  curves: number[][];
}

// A multiplier read off a small lookup curve: `v` samples the multiplier
// evenly from input `lo` to input `hi`, clamped outside that range.
export interface AffectCurve {
  lo: number;
  hi: number;
  v: number[];
}

interface TireCurvesJson {
  loads: number[];
  curves: number[][];
  compounds: {
    id: number;
    s: Record<Surface, Record<Direction, CurveBlockJson>>;
    // [wet grip scale, wet slip scale] per wet-capable surface.
    wet: Record<WetSurface, number[]>;
    // Key of the friction-against-pressure curve per surface.
    pressure: Record<Surface, number>;
    // Key of the friction-against-temperature curve, one per compound.
    temp: number;
  }[];
  pressure: {
    curves: Record<string, AffectCurve>;
    // Start pressure against peak slip, one curve shared by every compound.
    sasr: AffectCurve;
  };
}

export interface LoadCurve {
  scale: number;
  v: readonly number[];
}

export interface CurveBlock {
  maxslip: number;
  // The direction scale the game applies on top of the curve scale.
  ds: number;
  light: LoadCurve;
  heavy: LoadCurve;
}

// Rain exists on asphalt and offroad; the game data has no wet snow.
export type WetSurface = Exclude<Surface, "snow">;

export interface WetScales {
  // Multiplies grip on the wet surface.
  friction: number;
  // Multiplies the slip before the curve lookup, which pulls the peak earlier.
  slip: number;
}

export interface CompoundCurves {
  id: number;
  s: Record<Surface, Record<Direction, CurveBlock>>;
  wet: Record<WetSurface, WetScales>;
  // Grip multiplier against tire pressure in psi, per surface.
  pressure: Record<Surface, AffectCurve>;
  // Grip multiplier against tire temperature in degrees Fahrenheit.
  temp: AffectCurve;
}

export interface TireCurveData {
  // Wheel load in kilograms behind the light and the heavy curve.
  loads: [light: number, heavy: number];
  compounds: ReadonlyMap<number, CompoundCurves>;
  // Peak-slip multiplier against start pressure in psi, shared by every compound.
  peakSlipByPressure: AffectCurve;
}

function resolveBlock(json: TireCurvesJson, block: CurveBlockJson): CurveBlock {
  const curve = (ref: number[] | undefined): LoadCurve => {
    const index = ref?.[0];
    const scale = ref?.[1];
    const v = index === undefined ? undefined : json.curves[index];
    if (v === undefined || scale === undefined)
      throw new Error("tire-curves.json: dangling curve reference");
    return { scale, v };
  };
  return {
    maxslip: block.maxslip,
    ds: block.ds,
    light: curve(block.curves[0]),
    heavy: curve(block.curves[1]),
  };
}

function mapTireCurves(json: TireCurvesJson): TireCurveData {
  const [light, heavy] = json.loads;
  if (light === undefined || heavy === undefined)
    throw new Error("tire-curves.json: expected two wheel loads");
  const compounds = new Map<number, CompoundCurves>();
  for (const c of json.compounds) {
    const perDirection = (surface: Surface): Record<Direction, CurveBlock> => ({
      lat: resolveBlock(json, c.s[surface].lat),
      acc: resolveBlock(json, c.s[surface].acc),
      brk: resolveBlock(json, c.s[surface].brk),
    });
    const wetScales = (pair: number[]): WetScales => {
      const [friction, slip] = pair;
      if (friction === undefined || slip === undefined)
        throw new Error("tire-curves.json: expected a wet grip scale and a wet slip scale");
      return { friction, slip };
    };
    compounds.set(c.id, {
      id: c.id,
      s: {
        asphalt: perDirection("asphalt"),
        offroad: perDirection("offroad"),
        snow: perDirection("snow"),
      },
      wet: { asphalt: wetScales(c.wet.asphalt), offroad: wetScales(c.wet.offroad) },
      pressure: {
        asphalt: affectCurve(json, c.pressure.asphalt),
        offroad: affectCurve(json, c.pressure.offroad),
        snow: affectCurve(json, c.pressure.snow),
      },
      temp: affectCurve(json, c.temp),
    });
  }
  return { loads: [light, heavy], compounds, peakSlipByPressure: json.pressure.sasr };
}

function affectCurve(json: TireCurvesJson, id: number): AffectCurve {
  const curve = json.pressure.curves[String(id)];
  if (curve === undefined) throw new Error(`tire-curves.json: missing affect curve ${id}`);
  return curve;
}

// The curve table is served by the API rather than bundled, so that it can be
// withdrawn without a deploy.
// A 404 means it has been withdrawn, and the page renders a notice in place of
// the chart.
export async function loadTireCurves(): Promise<TireCurveData | null> {
  const res = await fetch("/api/tires/curves");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`tire-curves: request failed with ${res.status}`);
  return mapTireCurves((await res.json()) as TireCurvesJson);
}

// ── Series math ───────────────────────────────────────────────────────────────

// grip: the curve times every scale, in G; raw: the stored curve, peak at 1;
// scale: grip divided by raw, the G one unit of curve value buys.
export type CurveMode = "grip" | "raw" | "scale";

export interface Series {
  xs: number[];
  ys: number[];
  peakIndex: number;
  peakX: number;
  peakY: number;
  // Slip where the curve first drops under 90 % of its peak past the peak.
  plateauX: number;
  // Value at the end of the curve as a share of the peak.
  tail: number;
}

// Samples one compound at the given wheel load. The game keeps a curve at a
// light and at a heavy load and blends between them by load; the blend here is
// linear on the scaled values. Grip mode multiplies the curve by both scales,
// which ranks compounds on one surface; raw mode shows the unitless curve.
// Wet applies the compound's wet scales in grip mode on asphalt and offroad:
// grip is multiplied by the wet grip scale, and the slip is multiplied by the
// wet slip scale before the lookup, which pulls the peak earlier.
// Reading the slip scale as a scale on the input is an assumption the game
// data does not settle; a wet skidpad probe would.
// Pressure, in psi, applies the game's two pressure curves in grip mode: the
// compound's grip multiplier for the surface, and the shared peak-slip
// multiplier, which stretches the curve along the slip axis so a softer tire
// peaks later. Left undefined, the stored curve is shown as it is.
// Temperature, in degrees Fahrenheit, applies the compound's temperature grip
// curve the same way, as one more multiplier on the grip.
// Scale mode divides grip by the stored curve at the same slip, which isolates
// every scale from the curve shape: a flat line at the load of either stored
// curve, a gentle blend in between.
export function computeSeries(
  data: TireCurveData,
  compound: CompoundCurves,
  surface: Surface,
  dir: Direction,
  load: number,
  mode: CurveMode,
  wet = false,
  pressure?: number,
  temperature?: number,
): Series {
  const block = compound.s[surface][dir];
  const [lightLoad, heavyLoad] = data.loads;
  const w = Math.min(1, Math.max(0, (load - lightLoad) / (heavyLoad - lightLoad)));
  const scaled = mode !== "raw";
  const kl = scaled ? block.ds * block.light.scale : 1;
  const kh = scaled ? block.ds * block.heavy.scale : 1;
  const n = block.light.v.length;
  const dry: number[] = [];
  const dryRaw: number[] = [];
  for (let i = 0; i < n; i++) {
    const light = block.light.v[i] ?? 0;
    const heavy = block.heavy.v[i] ?? 0;
    dry.push((1 - w) * light * kl + w * heavy * kh);
    dryRaw.push((1 - w) * light + w * heavy);
  }
  const wetScales = wet && scaled && surface !== "snow" ? compound.wet[surface] : null;
  const gripByPressure =
    scaled && pressure !== undefined ? affectAt(compound.pressure[surface], pressure) : 1;
  const peakSlipByPressure =
    scaled && pressure !== undefined ? affectAt(data.peakSlipByPressure, pressure) : 1;
  const gripByTemperature =
    scaled && temperature !== undefined ? affectAt(compound.temp, temperature) : 1;
  // Wet compresses the slip axis, pressure stretches it; both resample the dry curve.
  const scales =
    wetScales || peakSlipByPressure !== 1 || gripByPressure !== 1 || gripByTemperature !== 1
      ? {
          friction: (wetScales?.friction ?? 1) * gripByPressure * gripByTemperature,
          slip: (wetScales?.slip ?? 1) / peakSlipByPressure,
        }
      : null;
  // Samples past the shown slip are dropped, so the tail and plateau describe
  // the shown range; the wet lookup still reads the full dry curve.
  const shown = Math.min(n, Math.floor((SLIP_SHOWN[dir] / block.maxslip) * (n - 1) + 1e-9) + 1);
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < shown; i++) {
    xs.push((i * block.maxslip) / (n - 1));
    ys.push(scales ? scales.friction * sampleAt(dry, i * scales.slip) : (dry[i] ?? 0));
  }
  if (mode === "scale") {
    // Where the stored curve is zero the ratio has no value; the blended scales stand in.
    const flat = ((1 - w) * kl + w * kh) * (scales?.friction ?? 1);
    for (let i = 0; i < shown; i++) {
      const raw = scales ? sampleAt(dryRaw, i * scales.slip) : (dryRaw[i] ?? 0);
      ys[i] = raw > 1e-6 ? (ys[i] ?? 0) / raw : flat;
    }
  }
  let peakIndex = 0;
  for (let i = 1; i < shown; i++) if ((ys[i] ?? 0) > (ys[peakIndex] ?? 0)) peakIndex = i;
  const peakY = ys[peakIndex] ?? 0;
  let plateauIndex = shown - 1;
  for (let i = peakIndex; i < shown; i++) {
    if ((ys[i] ?? 0) < 0.9 * peakY) {
      plateauIndex = i;
      break;
    }
  }
  return {
    xs,
    ys,
    peakIndex,
    peakX: xs[peakIndex] ?? 0,
    peakY,
    plateauX: xs[plateauIndex] ?? 0,
    tail: peakY > 0 ? (ys[shown - 1] ?? 0) / peakY : 0,
  };
}

// Linear interpolation of a uniformly sampled curve at a fractional index,
// holding the last value past the end of the curve.
function sampleAt(v: readonly number[], p: number): number {
  const last = v.length - 1;
  if (p >= last) return v[last] ?? 0;
  const lo = Math.floor(p);
  const f = p - lo;
  return (v[lo] ?? 0) * (1 - f) + (v[lo + 1] ?? 0) * f;
}

// Reads a lookup curve at an input, clamped to the curve's input range.
export function affectAt(curve: AffectCurve, input: number): number {
  const t = Math.min(1, Math.max(0, (input - curve.lo) / (curve.hi - curve.lo)));
  return sampleAt(curve.v, t * (curve.v.length - 1));
}
